/**
 * BeCool Address Checker Widget with Suburb Autocomplete
 *
 * Add this script to your Shopify theme to enable delivery availability checking.
 *
 * INSTALLATION:
 * 1. In Shopify Admin, go to Online Store > Themes > Edit code
 * 2. Find theme.liquid and add before </body>:
 *    <script src="https://cdn.shopify.com/s/files/1/XXXX/XXXX/t/XX/assets/becool-address-checker.js"></script>
 *
 * OR add inline in a section/page where you want the widget:
 *    <div id="becool-address-checker"></div>
 *    <script src="{{ 'becool-address-checker.js' | asset_url }}"></script>
 *
 * CUSTOMIZATION:
 * Set these before including the script to customize:
 *    window.BeCoolConfig = {
 *      containerId: 'becool-address-checker',  // Where to render the widget
 *      primaryColor: '#2c6ecb',                // Your brand color
 *      successColor: '#008060',                // Color for "We deliver!" message
 *      errorColor: '#d72c0d',                  // Color for "We don't deliver" message
 *      showOnCartPage: true,                   // Auto-show on /cart page
 *    };
 */

(function() {
  'use strict';

  // Default configuration
  const defaultConfig = {
    containerId: 'becool-address-checker',
    primaryColor: '#2563eb',
    successColor: '#008060',
    errorColor: '#d72c0d',
    showOnCartPage: true,
    showOnCheckout: false,
  };

  // Merge user config with defaults
  const config = Object.assign({}, defaultConfig, window.BeCoolConfig || {});

  // Session ID for correlating events per visitor
  const sessionId = Math.random().toString(36).substring(2, 10);

  // Determine page type from URL
  function getPageType() {
    const path = window.location.pathname;
    if (path === '/' || path === '') return 'home';
    if (path.includes('/cart')) return 'cart';
    if (path.includes('/products/')) return 'product';
    if (path.includes('/collections')) return 'collection';
    return 'other';
  }

  // Log widget events to backend (non-blocking, never throws)
  function logEvent(event, extra) {
    try {
      var payload = Object.assign({
        event: event,
        page_url: window.location.pathname,
        page_type: getPageType(),
        session_id: sessionId,
      }, extra || {});
      fetch('/apps/becool/widget-log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(function() {});
    } catch (e) {
      // Never break the widget
    }
  }

  // Cache for suburb lookups
  const suburbCache = {};

  // Widget HTML template
  function createWidget() {
    return `
      <style>
        .becool-widget {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          padding: 16px;
          border: 1px solid #e1e1e1;
          border-radius: 8px;
          margin: 16px 0;
          background: #fafafa;
          position: relative;
          z-index: 999999;
        }
        .becool-header {
          display: flex;
          align-items: center;
          margin-bottom: 12px;
        }
        .becool-header svg {
          margin-right: 8px;
          flex-shrink: 0;
        }
        .becool-header span {
          font-weight: 600;
          color: #333;
        }
        .becool-form {
          display: flex;
          align-items: stretch;
          gap: 8px;
          position: relative;
          z-index: 999999;
        }
        @media (max-width: 480px) {
          .becool-form {
            flex-direction: column;
          }
        }
        .becool-input-group {
          position: relative;
          flex: 1;
          z-index: 999999;
        }
        .becool-input-group.postcode {
          flex: 0 0 100px;
          min-width: 100px;
        }
        @media (max-width: 480px) {
          .becool-input-group.postcode {
            flex: 1;
          }
        }
        .becool-input {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #ccc;
          border-radius: 4px;
          font-size: 14px;
          box-sizing: border-box;
          background: #fff;
        }
        .becool-input:focus {
          outline: none;
          border-color: ${config.primaryColor};
        }
        .becool-input.suburb {
          text-transform: uppercase;
        }
        .becool-dropdown {
          position: absolute !important;
          top: 100% !important;
          left: 0;
          right: 0;
          max-height: 200px;
          overflow-y: auto;
          overflow-x: hidden;
          background: #fff !important;
          border: 1px solid #ccc;
          border-top: none;
          border-radius: 0 0 8px 8px;
          z-index: 2147483647 !important;
          display: none;
          box-shadow: 0 4px 16px rgba(0,0,0,0.2);
        }
        .becool-dropdown.open {
          display: block;
        }
        .becool-dropdown-item {
          padding: 10px 12px;
          cursor: pointer;
          font-size: 14px;
        }
        .becool-dropdown-item:hover,
        .becool-dropdown-item.selected {
          background: #f0f0f0;
        }
        .becool-check-btn {
          padding: 12px 24px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: #ffffff;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          letter-spacing: 0.3px;
          transition: all 0.3s ease;
          white-space: nowrap;
          flex-shrink: 0;
          box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
        }
        .becool-check-btn:hover {
          background: linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.5);
          transform: translateY(-1px);
        }
        .becool-check-btn:active {
          transform: translateY(0);
          box-shadow: 0 2px 6px rgba(102, 126, 234, 0.4);
        }
        .becool-check-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }
        .becool-result {
          margin-top: 12px;
          display: none;
        }
        .becool-result.show {
          display: block;
        }
        .becool-result-inner {
          display: flex;
          align-items: center;
          padding: 12px;
          border-radius: 4px;
        }
        .becool-result-inner.success {
          background: #e6f4ea;
          color: ${config.successColor};
        }
        .becool-result-inner.error {
          background: #fce8e6;
          color: ${config.errorColor};
        }
        .becool-result-inner.loading {
          color: #666;
        }
        .becool-result-icon {
          margin-right: 8px;
          display: flex;
          flex-shrink: 0;
        }
        .becool-result-message {
          font-weight: 500;
        }
        @keyframes becool-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .becool-spinner {
          animation: becool-spin 1s linear infinite;
        }
      </style>
      <div class="becool-widget">
        <div class="becool-header">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M20 8h-3V6c0-1.1-.9-2-2-2H9c-1.1 0-2 .9-2 2v2H4c-1.1 0-2 .9-2 2v10h20V10c0-1.1-.9-2-2-2zM9 6h6v2H9V6zm11 14H4v-8h3v2h2v-2h6v2h2v-2h3v8z" fill="${config.primaryColor}"/>
          </svg>
          <span>Check if we deliver to you</span>
        </div>

        <div class="becool-form">
          <div class="becool-input-group postcode">
            <input
              type="text"
              id="becool-postcode"
              class="becool-input"
              placeholder="Postcode"
              maxlength="4"
              inputmode="numeric"
            />
          </div>
          <div class="becool-input-group">
            <input
              type="text"
              id="becool-suburb"
              class="becool-input suburb"
              placeholder="Start typing suburb..."
              autocomplete="off"
            />
            <div id="becool-dropdown" class="becool-dropdown"></div>
          </div>
          <button id="becool-check-btn" class="becool-check-btn">
            Check
          </button>
        </div>

        <div id="becool-result" class="becool-result"></div>
      </div>
    `;
  }

  // Fetch suburbs for postcode
  async function fetchSuburbs(postcode) {
    if (suburbCache[postcode]) {
      return suburbCache[postcode];
    }

    try {
      const response = await fetch(`/apps/becool/suburbs?postcode=${postcode}`);
      if (response.ok) {
        const data = await response.json();
        suburbCache[postcode] = data.suburbs || [];
        return suburbCache[postcode];
      }
    } catch (error) {
      console.error('Error fetching suburbs:', error);
    }
    return [];
  }

  // Position the dropdown as fixed to the viewport so it can never be trapped
  // behind a section (the theme pins each section to its own z-index:0 layer) or
  // clipped by an ancestor's overflow. While open it lives on <body>.
  function positionDropdown() {
    const dropdown = document.getElementById('becool-dropdown');
    const input = document.getElementById('becool-suburb');
    if (!dropdown || !input || !dropdown.classList.contains('open')) return;
    const r = input.getBoundingClientRect();
    dropdown.style.setProperty('position', 'fixed', 'important');
    dropdown.style.setProperty('top', r.bottom + 'px', 'important');
    dropdown.style.setProperty('left', r.left + 'px', 'important');
    dropdown.style.setProperty('width', r.width + 'px', 'important');
    dropdown.style.setProperty('right', 'auto', 'important');
    dropdown.style.setProperty('z-index', '2147483647', 'important');
  }

  // Show dropdown with suburbs
  function showDropdown(suburbs, filter = '') {
    const dropdown = document.getElementById('becool-dropdown');
    if (!dropdown) return;

    const filtered = filter
      ? suburbs.filter(s => s.toUpperCase().includes(filter.toUpperCase()))
      : suburbs;

    if (filtered.length === 0) {
      dropdown.classList.remove('open');
      return;
    }

    dropdown.innerHTML = filtered.slice(0, 10).map((suburb, index) =>
      `<div class="becool-dropdown-item" data-suburb="${suburb}">${suburb}</div>`
    ).join('');

    dropdown.classList.add('open');

    // Escape the section stacking context: re-parent onto <body> and pin fixed.
    if (dropdown.parentNode !== document.body) { document.body.appendChild(dropdown); }
    positionDropdown();

    // Add click handlers
    dropdown.querySelectorAll('.becool-dropdown-item').forEach(item => {
      item.addEventListener('click', () => {
        const suburbInput = document.getElementById('becool-suburb');
        if (suburbInput) {
          suburbInput.value = item.dataset.suburb;
        }
        dropdown.classList.remove('open');
      });
    });
  }

  // Hide dropdown
  function hideDropdown() {
    const dropdown = document.getElementById('becool-dropdown');
    if (dropdown) {
      dropdown.classList.remove('open');
    }
  }

  // Show result message
  function showResult(type, message) {
    const resultDiv = document.getElementById('becool-result');
    if (!resultDiv) return;

    const icons = {
      success: '<svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"/></svg>',
      error: '<svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"/></svg>',
      loading: '<svg width="20" height="20" viewBox="0 0 24 24" class="becool-spinner"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" fill="none" stroke-dasharray="32" stroke-linecap="round"/></svg>'
    };

    resultDiv.innerHTML = `
      <div class="becool-result-inner ${type}">
        <span class="becool-result-icon">${icons[type]}</span>
        <span class="becool-result-message">${message}</span>
      </div>
    `;
    resultDiv.classList.add('show');
  }

  // Hide result
  function hideResult() {
    const resultDiv = document.getElementById('becool-result');
    if (resultDiv) {
      resultDiv.classList.remove('show');
    }
  }

  // Check delivery availability
  async function checkDelivery() {
    const postcodeInput = document.getElementById('becool-postcode');
    const suburbInput = document.getElementById('becool-suburb');
    const checkBtn = document.getElementById('becool-check-btn');

    const postcode = (postcodeInput?.value || '').trim();
    const suburb = (suburbInput?.value || '').trim().toUpperCase();

    // Validate postcode
    if (!postcode || !/^\d{4}$/.test(postcode)) {
      showResult('error', 'Please enter a valid 4-digit postcode');
      return;
    }

    // Validate suburb
    if (!suburb) {
      showResult('error', 'Please select or enter your suburb');
      return;
    }

    logEvent('check_clicked', { postcode: postcode, suburb: suburb });

    showResult('loading', 'Checking delivery availability...');
    if (checkBtn) checkBtn.disabled = true;

    try {
      const response = await fetch(`/apps/becool/check-address?postcode=${encodeURIComponent(postcode)}&suburb=${encodeURIComponent(suburb)}`);

      if (!response.ok) {
        throw new Error('Network error');
      }

      const data = await response.json();
      const resultMsg = data.message || (data.serviceable ? 'We deliver to your area!' : "Sorry, we don't deliver to this area yet");
      showResult(
        data.serviceable ? 'success' : 'error',
        resultMsg
      );
      logEvent(data.serviceable ? 'check_success' : 'check_fail', {
        postcode: postcode,
        suburb: suburb,
        message: resultMsg,
      });

    } catch (error) {
      console.error('BeCool address check error:', error);
      showResult('error', 'Unable to check delivery availability. Please try again.');
      logEvent('check_error', {
        postcode: postcode,
        suburb: suburb,
        message: error.message || 'Unknown error',
      });
    } finally {
      if (checkBtn) checkBtn.disabled = false;
    }
  }

  // Initialize widget
  function init() {
    let container = document.getElementById(config.containerId);

    if (!container) {
      // Auto-create container on cart page if configured
      if (config.showOnCartPage && window.location.pathname.includes('/cart')) {
        const cartForm = document.querySelector('form[action="/cart"]');
        if (cartForm) {
          const div = document.createElement('div');
          div.id = config.containerId;
          cartForm.parentNode.insertBefore(div, cartForm);
          container = div;
        }
      }
    }

    if (!container) return;

    // Render widget
    container.innerHTML = createWidget();

    // Log widget loaded event
    logEvent('widget_loaded');

    // --- Freight dropdown stacking fix ---------------------------------
    // The theme pins every section with `#content > * { z-index: 0 }`, which
    // traps this widget's absolutely-positioned suburb autocomplete inside
    // the section's stacking context, so it paints BEHIND the sections below.
    // Lift both the wrapping .shopify-section and the direct #content child
    // (inline !important) so the dropdown renders on top on every page.
    try {
      var _sec = container.closest('.shopify-section');
      if (_sec) {
        _sec.style.setProperty('position', 'relative', 'important');
        _sec.style.setProperty('z-index', '60', 'important');
      }
      var _host = container;
      while (_host && _host.parentElement) {
        if (_host.parentElement.id === 'content') {
          _host.style.setProperty('position', 'relative', 'important');
          _host.style.setProperty('z-index', '60', 'important');
          break;
        }
        _host = _host.parentElement;
      }
    } catch (e) {}
    // ------------------------------------------------------------------

    // Get elements
    const checkBtn = document.getElementById('becool-check-btn');
    const postcodeInput = document.getElementById('becool-postcode');
    const suburbInput = document.getElementById('becool-suburb');

    let currentSuburbs = [];
    let debounceTimer = null;

    // Check button click
    if (checkBtn) {
      checkBtn.addEventListener('click', checkDelivery);
    }

    // Postcode input - fetch suburbs when 4 digits entered
    if (postcodeInput) {
      postcodeInput.addEventListener('input', (e) => {
        e.target.value = e.target.value.replace(/[^\d]/g, '').slice(0, 4);

        if (e.target.value.length === 4) {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(async () => {
            currentSuburbs = await fetchSuburbs(e.target.value);
            if (currentSuburbs.length > 0) {
              // Auto-focus suburb input and show dropdown
              suburbInput.focus();
              showDropdown(currentSuburbs, suburbInput.value);
            }
          }, 300);
        } else {
          currentSuburbs = [];
          hideDropdown();
        }
      });
    }

    // Suburb input - filter dropdown as user types
    if (suburbInput) {
      suburbInput.addEventListener('input', (e) => {
        e.target.value = e.target.value.toUpperCase();
        if (currentSuburbs.length > 0) {
          showDropdown(currentSuburbs, e.target.value);
        }
      });

      suburbInput.addEventListener('focus', () => {
        if (currentSuburbs.length > 0) {
          showDropdown(currentSuburbs, suburbInput.value);
        }
      });

      suburbInput.addEventListener('blur', () => {
        // Delay to allow click on dropdown item
        setTimeout(hideDropdown, 200);
      });

      suburbInput.addEventListener('keydown', (e) => {
        const dropdown = document.getElementById('becool-dropdown');
        const items = dropdown?.querySelectorAll('.becool-dropdown-item');
        const selected = dropdown?.querySelector('.becool-dropdown-item.selected');

        if (e.key === 'ArrowDown' && items?.length) {
          e.preventDefault();
          if (selected) {
            selected.classList.remove('selected');
            const next = selected.nextElementSibling || items[0];
            next.classList.add('selected');
          } else {
            items[0].classList.add('selected');
          }
        } else if (e.key === 'ArrowUp' && items?.length) {
          e.preventDefault();
          if (selected) {
            selected.classList.remove('selected');
            const prev = selected.previousElementSibling || items[items.length - 1];
            prev.classList.add('selected');
          } else {
            items[items.length - 1].classList.add('selected');
          }
        } else if (e.key === 'Enter') {
          if (selected) {
            e.preventDefault();
            suburbInput.value = selected.dataset.suburb;
            hideDropdown();
          } else {
            checkDelivery();
          }
        } else if (e.key === 'Escape') {
          hideDropdown();
        }
      });
    }

    // Close dropdown when clicking outside (dropdown now lives on <body>)
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.becool-input-group') && !e.target.closest('#becool-dropdown')) {
        hideDropdown();
      }
    });

    // Keep the fixed dropdown aligned to the input as the page scrolls/resizes
    window.addEventListener('scroll', positionDropdown, true);
    window.addEventListener('resize', positionDropdown);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for manual initialization
  window.BeCoolAddressChecker = { init, checkDelivery };

})();